import React, {
  FC,
  ChangeEventHandler,
  FormEventHandler,
  useState,
  useCallback,
} from 'react';
import { useIntl } from 'react-intl';
import { LockIcon } from '@stpkg/stand-react';

import { useVerifyReservationFlowProduct } from 'src/hooks/api/reservation-flow/useVerifyReservationFlowProduct';
import { useRevalidateGetReservationFlowProduct } from 'src/hooks/api/reservation-flow/useGetReservationFlowProduct';
import { RSDetailHeader } from 'src/reservation-page/detail-header/RSDetailHeader';
import { RSFormMessageContainer } from 'src/reservation-page/forms/RSFormMessageContainer';
import { RSTextField } from 'src/reservation-page/forms/RSTextField';
import { RSButton } from 'src/reservation-page/RSButton';
import { RSDetailHeaderButtonLink } from 'src/reservation-page/detail-header/RSDetailHeaderButtonLink';
import { buildSiteUrl } from 'src/utils/request-url';

export interface PasswordVerifyTemplate {
  merchantPublicId: string;
  productPublicId: number;
  pageTitle: string;
}

export const PasswordVerifyTemplate: FC<PasswordVerifyTemplate> = ({
  merchantPublicId,
  productPublicId,
  pageTitle,
}) => {
  const intl = useIntl();

  const { isValidating, verifyReservationFlowProduct } =
    useVerifyReservationFlowProduct({
      parameter: {
        merchant_public_id: merchantPublicId,
        product_public_id: productPublicId,
      },
    });
  const { revalidateGetReservationFlowProduct } =
    useRevalidateGetReservationFlowProduct({
      parameter: {
        merchant_public_id: merchantPublicId,
        product_public_id: productPublicId,
      },
    });

  const [inputPassword, setInputPassword] = useState('');
  const [inputPasswordError, setInputPasswordError] = useState<
    { message: string } | undefined
  >(undefined);

  const onPasswordInputChange = useCallback<
    ChangeEventHandler<HTMLInputElement>
  >((event) => {
    setInputPassword(event.target.value);
  }, []);

  const onFormSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (inputPassword.length === 0) return;

      const result = await verifyReservationFlowProduct({
        values: { password: inputPassword },
      });

      if ('error' in result) {
        setInputPasswordError(result.error);
        return;
      }

      await revalidateGetReservationFlowProduct();
    },
    [
      inputPassword,
      verifyReservationFlowProduct,
      revalidateGetReservationFlowProduct,
    ],
  );

  return (
    <>
      <div className="z-5 sticky top-0">
        <RSDetailHeader
          title={pageTitle}
          leftButton={
            <RSDetailHeaderButtonLink
              href={buildSiteUrl(`/products/${productPublicId}`)}
              variant="close"
            >
              {intl.formatMessage({
                defaultMessage: '戻る',
                id: 'rzWf7B',
                description:
                  'src/templates/merchants/PasswordVerifyTemplate/index.tsx',
              })}
            </RSDetailHeaderButtonLink>
          }
        />
      </div>

      <div className="pc:px-4 mx-auto box-border max-w-[560px] px-2.5 pb-9 pt-5">
        <div className="mb-6 flex flex-col items-center">
          <div className="mb-3">
            <LockIcon
              width="80"
              height="80"
              viewBox="0 0 48 48"
              className="fill-gray-1000"
            />
          </div>

          <div className="flex flex-col items-center">
            <div className="mb-1">
              <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
                {intl.formatMessage({
                  defaultMessage: 'パスワードが設定されています',
                  id: 'kaTBH1',
                  description:
                    'src/templates/merchants/PasswordVerifyTemplate/index.tsx',
                })}
              </div>
            </div>
            <div>
              <div className="rs-font text-13 text-gray-1000 pc:text-12 text-center font-normal">
                {intl.formatMessage(
                  {
                    defaultMessage:
                      'このページを閲覧するには<br></br>パスワードを入力してください',
                    id: 'KS7f2q',
                    description:
                      'src/templates/merchants/PasswordVerifyTemplate/index.tsx',
                  },
                  {
                    br: () => <br></br>,
                  },
                )}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={onFormSubmit}>
          <div className="mb-2.5">
            <RSFormMessageContainer
              isBlock
              errors={
                typeof inputPasswordError !== 'undefined'
                  ? [inputPasswordError.message]
                  : []
              }
            >
              <RSTextField
                labelText={intl.formatMessage({
                  defaultMessage: 'パスワード',
                  id: 'Uixg8O',
                  description:
                    'src/templates/merchants/PasswordVerifyTemplate/index.tsx',
                })}
                type="password"
                isBlock
                onChange={onPasswordInputChange}
              />
            </RSFormMessageContainer>
          </div>

          <div>
            <RSButton
              variant="large"
              isBlock
              isLoading={isValidating}
              disabled={inputPassword.length === 0 || isValidating}
            >
              {intl.formatMessage({
                defaultMessage: '次へ',
                id: 'v6/cQE',
                description:
                  'src/templates/merchants/PasswordVerifyTemplate/index.tsx',
              })}
            </RSButton>
          </div>
        </form>
      </div>
    </>
  );
};
